import classNames from 'classnames';
import React from 'react';
import { Modal as MuiModal, ModalProps as _MuiModalProps, Fade } from '@material-ui/core';
import _uniqueId from 'lodash/uniqueId';
import Button from '../../atoms/Button';

// 中継用のinterface. childrenのタイプエラーを出さないようにする
// https://github.com/Microsoft/TypeScript/issues/3402
interface MuiModalProps extends _MuiModalProps {
  children: any;
}

export interface ModalProps extends MuiModalProps {
  className?: string;

  /**
   * モーダル表面のクラス名
   */
  surfaceClassName?: string;

  /**
   * 開いているかどうか
   */
  open: boolean;

  /**
   * モーダル内のスクロール時にフェードを表示するか
   */
  hasFade?: boolean;

  children: React.ReactNode;
}

/**
 * Modal
 * see https://material-ui.com/api/modal/
 */
class Modal extends React.Component<ModalProps> {
  static Header: typeof ModalHeader;
  static Content: typeof ModalContent;
  static Actions: typeof ModalActions;

  constructor(props: ModalProps) {
    super(props);
  }

  render() {
    const { className, surfaceClassName, open = false, hasFade = false, onClose, children: _children, ...props } = this.props;

    const classes = classNames('uc-modal', 'uc-modal--open', className, !hasFade && 'uc-modal--no-fade');

    const titleId = _uniqueId('modal-title-');
    const descriptionId = _uniqueId('modal-description-');

    // children にPropsを伝搬する
    const children = React.Children.map(_children, (child: React.ReactNode) => {
      return React.cloneElement(child as React.ReactElement<ModalHeaderProps | ModalContentProps | ModalActionsProps>, {
        'aria-labelledby': titleId,
        'aria-describedby': descriptionId,
        onClose,
      });
    });

    return (
      <MuiModal {...props} open={open} aria-labelledby={titleId} aria-describedby={descriptionId} className={classes}>
        <Fade in={open}>
          <div className='uc-modal__container'>
            <div className={classNames('uc-modal__surface', surfaceClassName)}>{children}</div>
          </div>
        </Fade>
      </MuiModal>
    );
  }
}

export interface ModalHeaderProps extends React.HTMLAttributes<HTMLDivElement> {
  /**
   * 閉じるボタンがあるかどうか
   */
  hasCloseButton?: boolean;

  onClose?: (event: {}, reason: 'backdropClick' | 'escapeKeyDown') => void;

  onClickClose?: () => void;
}

const ModalHeader: React.FC<ModalHeaderProps> = ({
  className,
  children,
  hasCloseButton = true,
  onClose,
  onClickClose,
  'aria-labelledby': ariaLabelledby,
  ...props
}) => {
  return (
    <div className={classNames('uc-modal__header', className)} {...props}>
      <h2 id={ariaLabelledby} className='uc-modal__header-title'>
        {children}
      </h2>
      {hasCloseButton && (
        <Button
          className='uc-modal__close-button'
          data-testid='modal-header-close-btn'
          data-testid-e2e='modalHeader-closeBtn'
          leftIcon='close'
          onClick={(e) => {
            onClose && onClose(e, 'backdropClick');
            onClickClose && onClickClose();
          }}
        />
      )}
    </div>
  );
};

export type ModalContentProps = React.HTMLAttributes<HTMLDivElement>;

const ModalContent: React.FC<ModalContentProps> = ({ className, 'aria-describedby': ariaDescribedby, children, ...props }) => {
  const classes = classNames('uc-modal__content', className);

  return (
    <div id={ariaDescribedby} className={classes} {...props}>
      {children}
    </div>
  );
};

export type ModalActionsProps = React.HTMLAttributes<HTMLDivElement>;

const ModalActions: React.FC<ModalActionsProps> = ({ className, children, ...props }) => {
  const classes = classNames('uc-modal__actions', className);

  return (
    <div className={classes} {...props}>
      {children}
    </div>
  );
};

Modal.Header = ModalHeader;
Modal.Content = ModalContent;
Modal.Actions = ModalActions;

export default Modal;
